import { BarChart, type BarChartProps } from '../charts/BarChart';
import type { BarDatum } from '../charts/model';
import { usePublishedPrices } from '../../features/catalog/queries';
import { quotePrice, type PriceSpec } from '../../lib/contentPrice';

/** A bar whose value is AnyAPI's published price, e.g.
 *  `{ label: "AnyAPI", sku: "google.search", calls: 100000 }`. */
type PricedBar = PriceSpec & { label: string };

const isPriced = (bar: BarDatum | PricedBar): bar is PricedBar => 'sku' in bar;

export interface PricedBarChartProps extends Omit<BarChartProps, 'data'> {
  data: (BarDatum | PricedBar)[];
}

/**
 * The `BarChart` posts get: a bar may name a SKU instead of typing a value.
 * When one does, the typed `ariaLabel` is only the chart's title and every
 * bar's label and value is appended to it, so the one number a post may not
 * type cannot survive in the accessible name either.
 *
 * A priced bar is left out only when a live navigation could not reach the
 * catalog, or in the content sandbox build, which has none.
 */
export const PricedBarChart = ({ data, ...chart }: PricedBarChartProps) => {
  const prices = usePublishedPrices();
  const priced = data.some(isPriced);
  const bars = data.flatMap((bar): BarDatum[] => {
    if (!isPriced(bar)) return [bar];
    return prices ? [{ label: bar.label, value: quotePrice(prices, bar).value }] : [];
  });
  return <BarChart {...chart} data={bars} ariaValues={priced} />;
};
